/**
 * Cutoff for the Glass Box "Load earlier events" button (Spec 167 Phase 4).
 *
 * The backfill route returns events BEFORE the cutoff, so the cutoff must be the
 * earliest event the panel already shows for the node.
 */

import type { GlassBoxItem } from '../hooks/useGlassBoxEvents';

/**
 * An event's own time: `data.timestamp` (ISO string or epoch ms) when present and
 * parseable, else `fallbackMs`. `receivedAt` is browser receive time (live) or Axiom
 * `_time` (backfilled), so neither is the event's own time.
 */
export function eventOwnTimeMs(data: Record<string, unknown> | undefined, fallbackMs: number): number {
  const raw = data?.timestamp;
  const ms = typeof raw === 'number' ? raw : typeof raw === 'string' ? Date.parse(raw) : NaN;
  return Number.isFinite(ms) ? ms : fallbackMs;
}

/**
 * Cutoff (ISO8601) the backfill route is asked to look before: the earliest buffered
 * event time for `nodeId`, or `nowMs` when the node has no buffered events (everything
 * Axiom holds for it is then "earlier").
 *
 * No skew is applied: for live events `receivedAt` is within network latency of the
 * event's own time, and the route compares against Axiom `_time`, which lags the
 * event, so events a few batch-delays before the cutoff can be missed (Spec 167 PR-B note).
 */
export function loadEarlierCutoff(events: GlassBoxItem[], nodeId: string, nowMs: number): string {
  let earliest = nowMs;
  for (const e of events) {
    if (e.data?.node_id !== nodeId) continue;
    earliest = Math.min(earliest, eventOwnTimeMs(e.data, e.receivedAt));
  }
  return new Date(earliest).toISOString();
}
